import { ECRCode } from '../types/ecrCode';
import { apiRequest, safeAcquireTokenSilent, safeGetAllAccounts } from '../auth/authConfig';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Helper function to get auth headers
const getAuthHeaders = async (): Promise<Record<string, string>> => {
  try {
    const accounts = await safeGetAllAccounts();
    
    if (accounts.length > 0) {
      const response = await safeAcquireTokenSilent();
      
      if (response.accessToken) {
        return {
          'Authorization': `Bearer ${response.accessToken}`,
          'Content-Type': 'application/json'
        };
      }
    }
    
    return { 'Content-Type': 'application/json' };
  } catch (error) {
    console.error('Error getting auth token:', error);
    return { 'Content-Type': 'application/json' };
  }
};

export const ecrCodeService = {
  // Alle ECR Codes laden
  async getAllCodes(): Promise<ECRCode[]> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/ecr-codes`, { headers });
      if (!response.ok) {
        throw new Error('Failed to fetch ECR codes');
      }
      const result = await response.json();
      // API returns { data: hierarchy, total: count }, we need just the data array
      return result.data || result || [];
    } catch (error) {
      console.error('Error fetching ECR codes:', error);
      return [];
    }
  },

  // ECR Code-Hierarchie laden
  async getHierarchy(): Promise<ECRCode[]> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/ecr-codes/hierarchy`, { headers });
      if (!response.ok) {
        throw new Error('Failed to fetch ECR code hierarchy');
      }
      const result = await response.json();
      // API returns { data: hierarchy, total: count }, we need just the data array
      return result.data || result || [];
    } catch (error) {
      console.error('Error fetching ECR code hierarchy:', error);
      return [];
    }
  },

  // ECR Codes suchen
  async searchCodes(query: string): Promise<ECRCode[]> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/ecr-codes/search?q=${encodeURIComponent(query)}`, { headers });
      if (!response.ok) {
        throw new Error('Failed to search ECR codes');
      }
      const result = await response.json();
      // API returns { data: codes, total: count }, we need just the data array
      return result.data || result || [];
    } catch (error) {
      console.error('Error searching ECR codes:', error);
      return [];
    }
  },

  // ECR Code Details
  async getCodeDetails(code: string): Promise<ECRCode | null> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/ecr-codes/${encodeURIComponent(code)}`, { headers });
      if (!response.ok) {
        return null;
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching ECR code details:', error);
      return null;
    }
  },

  // ECR Codes importieren
  async importCodes(codes: ECRCode[]): Promise<{ message: string; count: number }> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/ecr-codes/import`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ codes })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to import ECR codes: ${response.status} ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error importing ECR codes:', error);
      throw error;
    }
  }
};
